import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import AuthLoader from '../../Components/Helper/AuthLoader';
import { setUserFromLocalStorage, logOut } from '../../Store/userSlice';
import { clearAllAuthStorage } from '../../utils/localStorageKeys';
import { isUpscContext } from '../../utils/trackResolver';

/** Decode JWT payload without verifying signature (client-side expiry check only). */
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ? payload.exp < Date.now() / 1000 : false;
  } catch {
    return false; // Can't decode — let the backend decide
  }
};

const LoginCheck = ({ element }) => {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Dispatch action to load user from localStorage if not already loaded
    if (!user || !user.name) {
      dispatch(setUserFromLocalStorage());
    }

    // Delay rendering until the user is loaded
    setIsLoaded(true);
  }, [dispatch, user]);

  // Prevent rendering if user state is not loaded yet
  if (!isLoaded) {
    return <AuthLoader />;
  }

  // Client-side expiry check: if the stored JWT is already expired, clear
  // the session silently and redirect to login WITHOUT showing the
  // "session expired" toast. This prevents the jarring sequence of
  // "route guard lets you to /myprofile → first API call 401 → toast appears".
  // UPSC aspirants get their own login; everyone else the main teacher login.
  const loginPath = isUpscContext() ? '/upsc/signin' : '/teacher-login';

  const storedToken = localStorage.getItem('token');
  if (storedToken && isTokenExpired(storedToken)) {
    clearAllAuthStorage();
    dispatch(logOut());
    return <Navigate to={loginPath} replace />;
  }

  // If the user is not authenticated, redirect to login page
  if (!user || !user.name) {
    return <Navigate to={loginPath} replace />;
  }

  // If user is authenticated, render the requested element
  return element;
};

LoginCheck.propTypes = {
  element: PropTypes.node.isRequired,
};

export default LoginCheck;
